import React, { useEffect, useState, useRef } from "react";
import { ChevronDown, ChevronUp, Save, Plus, Trash2, Upload, Image as ImageIcon, X, MapPin, Building2, Video, Sparkles, Check, HelpCircle } from "lucide-react";
import api from "../../lib/api";
import { uploadVideoChunked } from "../../lib/uploadVideo";
import { useApp } from "../../context/AppContext";
import { toast } from "sonner";
import { AMENITY_DEFS } from "../../lib/amenities";
import { resolveImageUrl } from "../../lib/imageUrl";
import LocationPicker from "../../components/LocationPicker";
import ImageDropzone from "../../components/ImageDropzone";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HotelEditor = ({ hotel, idx, hotels, setHotels, onSave, onDelete }) => {
    const [expanded, setExpanded] = useState(false);
    const [videoUploading, setVideoUploading] = useState(false);
    const [videoProgress, setVideoProgress] = useState(0);
    const [newImgUrl, setNewImgUrl] = useState("");
    const [locPicker, setLocPicker] = useState({ open: false, idx: 0 });
    const [seasonalForm, setSeasonalForm] = useState({});  // { roomIdx: { price_individual, price_bulk, months:{1:true,...} } }
    const [rangeForm, setRangeForm] = useState({});  // { roomIdx: { start, end, price_individual, price_bulk } }
    const videoInputRef = useRef(null);

    const update = (field, value) => {
        const n = [...hotels];
        n[idx][field] = value;
        setHotels(n);
    };

    const updateRoom = (roomIdx, field, value) => {
        const n = [...hotels];
        n[idx].rooms[roomIdx][field] = field === "name" ? value : Number(value) || 0;
        setHotels(n);
    };

    // Meal plan helpers (dynamic add/edit/remove per room)
    const addMealPlan = (roomIdx) => {
        const n = [...hotels];
        const r = n[idx].rooms[roomIdx];
        r.meal_plans = [...(r.meal_plans || []), { id: `mp_${Date.now()}_${Math.random().toString(36).slice(2,7)}`, label: "Yeni Yemek Seçeneği", price: 0 }];
        setHotels(n);
    };
    const updateMealPlan = (roomIdx, mpIdx, field, value) => {
        const n = [...hotels];
        const mp = n[idx].rooms[roomIdx].meal_plans[mpIdx];
        mp[field] = field === "price" ? (Number(value) || 0) : value;
        setHotels(n);
    };
    const removeMealPlan = (roomIdx, mpIdx) => {
        const n = [...hotels];
        n[idx].rooms[roomIdx].meal_plans.splice(mpIdx, 1);
        setHotels(n);
    };
    const seedDefaultMealPlans = (roomIdx) => {
        const n = [...hotels];
        const r = n[idx].rooms[roomIdx];
        const seeded = [
            { label: "Kahvaltı", price: Number(r.meal_breakfast) || 0 },
            { label: "Yarım Pansiyon", price: Number(r.meal_half_board) || 0 },
            { label: "Tam Pansiyon", price: Number(r.meal_full_board) || 0 },
        ].map((p) => ({ id: `mp_${Date.now()}_${Math.random().toString(36).slice(2,7)}`, ...p }));
        r.meal_plans = seeded;
        setHotels(n);
    };

    // ---------- Seasonal (monthly) pricing helpers ----------
    const getSeasonalForm = (ri) => seasonalForm[ri] || { price_individual: "", price_bulk: "", months: {} };
    const updateSeasonalForm = (ri, patch) => {
        setSeasonalForm((cur) => ({ ...cur, [ri]: { ...getSeasonalForm(ri), ...patch } }));
    };
    const toggleMonth = (ri, m) => {
        const f = getSeasonalForm(ri);
        const next = { ...(f.months || {}) };
        next[m] = !next[m];
        updateSeasonalForm(ri, { months: next });
    };
    const applySeasonal = (ri) => {
        const f = getSeasonalForm(ri);
        const pi = Number(f.price_individual) || 0;
        const pb = Number(f.price_bulk) || 0;
        const selectedMonths = Object.entries(f.months || {}).filter(([, v]) => v).map(([k]) => Number(k));
        if (selectedMonths.length === 0) { alert("Lütfen en az bir ay seçin"); return; }
        if (pi <= 0 && pb <= 0) { alert("Bireysel veya toplu fiyattan en az birini girin"); return; }
        const n = [...hotels];
        const r = n[idx].rooms[ri];
        const existing = r.seasonal_prices || [];
        const map = {};
        existing.forEach((s) => { map[s.month] = s; });
        selectedMonths.forEach((m) => {
            map[m] = {
                month: m,
                price_individual: pi > 0 ? pi : (map[m]?.price_individual || 0),
                price_bulk: pb > 0 ? pb : (map[m]?.price_bulk || 0),
            };
        });
        r.seasonal_prices = Object.values(map).sort((a, b) => a.month - b.month);
        setHotels(n);
        updateSeasonalForm(ri, { price_individual: "", price_bulk: "", months: {} });
    };
    const removeSeasonal = (ri, m) => {
        const n = [...hotels];
        const r = n[idx].rooms[ri];
        r.seasonal_prices = (r.seasonal_prices || []).filter((s) => s.month !== m);
        setHotels(n);
    };

    // ---------- Date-range pricing helpers ----------
    const getRangeForm = (ri) => rangeForm[ri] || { start: "", end: "", price_individual: "", price_bulk: "" };
    const updateRangeForm = (ri, patch) => setRangeForm((cur) => ({ ...cur, [ri]: { ...getRangeForm(ri), ...patch } }));
    const applyRange = (ri) => {
        const f = getRangeForm(ri);
        const pi = Number(f.price_individual) || 0;
        const pb = Number(f.price_bulk) || 0;
        if (!f.start || !f.end) { alert("Başlangıç ve bitiş tarihini seçin"); return; }
        if (f.end < f.start) { alert("Bitiş tarihi başlangıçtan önce olamaz"); return; }
        if (pi <= 0 && pb <= 0) { alert("Bireysel veya toplu fiyattan en az birini girin"); return; }
        const n = [...hotels];
        const r = n[idx].rooms[ri];
        r.date_range_prices = [...(r.date_range_prices || []), { start: f.start, end: f.end, price_individual: pi, price_bulk: pb }]
            .sort((a, b) => (a.start < b.start ? -1 : 1));
        setHotels(n);
        setRangeForm((cur) => ({ ...cur, [ri]: { start: "", end: "", price_individual: "", price_bulk: "" } }));
    };
    const removeRange = (ri, rpi) => {
        const n = [...hotels];
        const r = n[idx].rooms[ri];
        r.date_range_prices = (r.date_range_prices || []).filter((_, k) => k !== rpi);
        setHotels(n);
    };

    const addRoom = () => {
        const n = [...hotels];
        n[idx].rooms.push({
            id: "",
            name: "Yeni Oda",
            capacity: 2,
            price_individual: 0,
            price_bulk: 0,
            meal_breakfast: 0,
            meal_half_board: 0,
            meal_full_board: 0,
            meal_plans: [],
            seasonal_prices: [],
            date_range_prices: [],
            inventory: 5,
        });
        setHotels(n);
    };

    const removeRoom = (roomIdx) => {
        if (!confirm("Bu oda tipini silmek istediğinize emin misiniz?")) return;
        const n = [...hotels];
        n[idx].rooms.splice(roomIdx, 1);
        setHotels(n);
    };

    const addImageUrl = () => {
        if (!newImgUrl.trim()) return;
        const n = [...hotels];
        n[idx].images = [...(n[idx].images || []), newImgUrl.trim()];
        setHotels(n);
        setNewImgUrl("");
    };

    const removeImage = (imgIdx) => {
        const n = [...hotels];
        n[idx].images.splice(imgIdx, 1);
        setHotels(n);
    };

    const moveImage = (imgIdx, dir) => {
        const n = [...hotels];
        const arr = n[idx].images;
        const newIdx = imgIdx + dir;
        if (newIdx < 0 || newIdx >= arr.length) return;
        [arr[imgIdx], arr[newIdx]] = [arr[newIdx], arr[imgIdx]];
        setHotels(n);
    };

    const handleVideoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 300 * 1024 * 1024) { toast.error("Video 300MB'tan büyük olamaz"); return; }
        setVideoUploading(true);
        setVideoProgress(0);
        try {
            const resp = await uploadVideoChunked(file, setVideoProgress);
            update("video_url", resp.url); // store relative; resolve at render
            toast.success("Video yüklendi");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Video yüklenemedi");
        } finally {
            setVideoUploading(false);
            setVideoProgress(0);
            if (videoInputRef.current) videoInputRef.current.value = "";
        }
    };

    const totalInventory = hotel.rooms?.reduce((s, r) => s + (Number(r.inventory) || 0), 0) || 0;

    return (
        <div className="border border-border_green bg-emerald_surface">
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-2 text-left hover:bg-emerald_hover transition-colors"
                data-testid={`hotel-toggle-${hotel.id}`}
            >
                <div className="flex items-center gap-3 sm:gap-5 min-w-0">
                    {hotel.images?.[0] ? (
                        <img src={resolveImageUrl(hotel.images[0])} alt="" className="w-12 h-12 sm:w-16 sm:h-16 object-cover border border-border_green flex-shrink-0" />
                    ) : (
                        <div className="w-12 h-12 sm:w-16 sm:h-16 border border-border_green bg-emerald_deep flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-6 h-6 text-muted_green" />
                        </div>
                    )}
                    <div className="hidden sm:block text-[10px] uppercase tracking-[0.25em] text-gold px-2 py-1 border border-gold/40 flex-shrink-0">
                        {hotel.city === "Mecca" ? "Mekke" : "Medine"}
                    </div>
                    <div className="min-w-0">
                        <div className="font-serif text-lg sm:text-xl truncate">
                            {hotel.name}
                            <span className="sm:hidden text-[10px] uppercase tracking-[0.15em] text-gold ml-2 align-middle">{hotel.city === "Mecca" ? "Mekke" : "Medine"}</span>
                        </div>
                        <div className="text-xs text-muted_green mt-1 flex items-center gap-x-2 gap-y-0.5 flex-wrap">
                            <span className="whitespace-nowrap">{hotel.rooms?.length || 0} oda tipi</span>
                            <span className="whitespace-nowrap">· {totalInventory} oda</span>
                            <span className="hidden sm:flex items-center gap-1">· <ImageIcon className="w-3 h-3" /> {hotel.images?.length || 0}</span>
                            <span className="hidden sm:inline whitespace-nowrap">· {hotel.distance_to_haram}</span>
                        </div>
                    </div>
                </div>
                {expanded ? <ChevronUp className="w-4 h-4 text-gold flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gold flex-shrink-0" />}
            </button>

            {expanded && (
                <div className="p-4 sm:p-6 border-t border-border_green space-y-8">
                    {/* BASIC */}
                    <div className="grid lg:grid-cols-2 gap-4">
                        <div>
                            <label className="label-dark">Otel Adı</label>
                            <input className="input-dark" value={hotel.name} onChange={(e) => update("name", e.target.value)} data-testid={`name-${hotel.id}`} />
                        </div>
                        <div>
                            <label className="label-dark">Harem'e Mesafe</label>
                            <input className="input-dark" value={hotel.distance_to_haram} onChange={(e) => update("distance_to_haram", e.target.value)} placeholder="100 m" />
                        </div>
                        <div>
                            <label className="label-dark">Mesafe Notu (A+/A-/B+/B-/C)</label>
                            <select className="input-dark" value={hotel.distance_grade || ""} onChange={(e) => update("distance_grade", e.target.value)} data-testid={`grade-select-${hotel.id}`}>
                                <option value="" className="bg-emerald_surface">— Seçiniz —</option>
                                {["A+", "A", "A-", "B+", "B", "B-", "C"].map((g) => (
                                    <option key={g} value={g} className="bg-emerald_surface">{g}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="label-dark">Yıldız Sayısı</label>
                            <select className="input-dark" value={hotel.star_rating || 5} onChange={(e) => update("star_rating", Number(e.target.value))}>
                                {[3, 4, 5].map((s) => <option key={s} value={s} className="bg-emerald_surface">{s} ★</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="label-dark">Gösterim İndirimi (%)</label>
                            <input type="number" min={0} max={90} className="input-dark" value={hotel.discount_percent || 0} onChange={(e) => update("discount_percent", Number(e.target.value))} data-testid={`discount-${hotel.id}`} />
                            <div className="text-[10px] text-muted_green mt-1">0 = kapalı. Sitede üzeri çizili "eski fiyat" + indirim rozeti gösterilir (örn. %10-15).</div>
                        </div>
                        <div>
                            <label className="label-dark">Şehir</label>
                            <select className="input-dark" value={hotel.city} onChange={(e) => update("city", e.target.value)}>
                                <option value="Mecca" className="bg-emerald_surface">Mekke</option>
                                <option value="Medina" className="bg-emerald_surface">Medine</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="label-dark">Açıklama</label>
                        <textarea rows={3} className="input-dark" value={hotel.description} onChange={(e) => update("description", e.target.value)} />
                    </div>

                    {/* PHOTOS */}
                    <div className="border border-border_green p-5 space-y-4">
                        <div className="text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-2">
                            <ImageIcon className="w-3.5 h-3.5" /> Fotoğraflar ({hotel.images?.length || 0})
                        </div>

                        <ImageDropzone
                            testId={`dropzone-${hotel.id}`}
                            onUploaded={(urls) => {
                                const n = [...hotels];
                                n[idx].images = [...(n[idx].images || []), ...urls];
                                setHotels(n);
                            }}
                        />

                        <div className="flex gap-2">
                            <input
                                className="input-dark flex-1"
                                placeholder="veya doğrudan görsel URL'si yapıştır"
                                value={newImgUrl}
                                onChange={(e) => setNewImgUrl(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addImageUrl())}
                                data-testid={`url-input-${hotel.id}`}
                            />
                            <button type="button" onClick={addImageUrl} className="btn-outline-gold !py-2 !px-3 !text-[11px]" data-testid={`url-add-${hotel.id}`}>
                                <Plus className="w-3.5 h-3.5" /> Ekle
                            </button>
                        </div>

                        {hotel.images?.length > 0 ? (
                            <div className="grid grid-cols-3 lg:grid-cols-5 gap-3">
                                {hotel.images.map((img, i) => (
                                    <div key={img} className="relative group border border-border_green">
                                        <img src={resolveImageUrl(img)} alt="" className="w-full h-24 object-cover" />
                                        <div className="absolute inset-0 bg-emerald_deep/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => moveImage(i, -1)}
                                                disabled={i === 0}
                                                className="text-gold hover:text-bone disabled:opacity-30"
                                                title="Sola al"
                                            >
                                                ←
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => moveImage(i, 1)}
                                                disabled={i === hotel.images.length - 1}
                                                className="text-gold hover:text-bone disabled:opacity-30"
                                                title="Sağa al"
                                            >
                                                →
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => removeImage(i)}
                                                className="text-red-400 hover:text-red-300"
                                                title="Sil"
                                                data-testid={`img-del-${hotel.id}-${i}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                        {i === 0 && (
                                            <div className="absolute top-1 left-1 text-[9px] uppercase tracking-wider bg-gold text-emerald_deep px-1.5 py-0.5">
                                                Kapak
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-xs text-muted_green text-center py-6 border border-dashed border-border_green">
                                Henüz fotoğraf eklenmedi. Yükleyin veya URL yapıştırın.
                            </div>
                        )}
                    </div>

                    {/* HOTEL VIDEO */}
                    <div className="border border-border_green p-5 space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-2">
                                <Video className="w-3.5 h-3.5" /> Otel Tanıtım Videosu
                            </div>
                            <input
                                ref={videoInputRef}
                                type="file"
                                accept="video/mp4,video/webm,video/quicktime"
                                onChange={handleVideoUpload}
                                className="hidden"
                                data-testid={`hotel-video-input-${hotel.id}`}
                            />
                            {hotel.video_url ? (
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => videoInputRef.current?.click()} disabled={videoUploading} className="btn-outline-gold !py-2 !px-3 !text-[11px]">
                                        <Upload className="w-3.5 h-3.5" /> {videoUploading ? `Yükleniyor… %${videoProgress}` : "Değiştir"}
                                    </button>
                                    <button type="button" onClick={() => update("video_url", "")} className="btn-outline-gold !py-2 !px-3 !text-[11px] !text-red-400 !border-red-500/40">
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ) : (
                                <button type="button" onClick={() => videoInputRef.current?.click()} disabled={videoUploading} className="btn-outline-gold !py-2 !px-3 !text-[11px]" data-testid={`hotel-video-upload-${hotel.id}`}>
                                    <Upload className="w-3.5 h-3.5" /> {videoUploading ? `Yükleniyor… %${videoProgress}` : "Video Yükle"}
                                </button>
                            )}
                        </div>
                        {hotel.video_url ? (
                            <video src={hotel.video_url} controls className="w-full max-h-[220px] object-contain bg-emerald_deep border border-border_green" />
                        ) : (
                            <div className="text-xs text-muted_green">
                                Bu otele özel tanıtım videosu yükleyin. MP4/WEBM · max 300 MB. Otel detay sayfasında "Tanıtım Videosu" sekmesinde görünecek.
                            </div>
                        )}
                    </div>

                    {/* LOCATION */}
                    <div className="border border-border_green p-5 space-y-3">
                        <div className="text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-2">
                            <MapPin className="w-3.5 h-3.5" /> Konum Bilgileri
                        </div>
                        <div>
                            <label className="label-dark">Adres</label>
                            <input className="input-dark" placeholder="Tam adres" value={hotel.address || ""} onChange={(e) => update("address", e.target.value)} data-testid={`addr-${hotel.id}`} />
                        </div>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="text-[10px] uppercase tracking-[0.15em] text-gold">Konum (harita üzerinde)</div>
                            <button
                                type="button"
                                onClick={() => setLocPicker({ open: true, idx })}
                                className="btn-outline-gold !text-xs"
                                data-testid={`open-loc-picker-${hotel.id}`}
                            >
                                <MapPin className="w-3.5 h-3.5" /> Haritadan Seç
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="label-dark">Enlem (Latitude)</label>
                                <input type="number" step="any" className="input-dark" placeholder="21.4225" value={hotel.latitude ?? ""} onChange={(e) => update("latitude", e.target.value === "" ? null : Number(e.target.value))} />
                            </div>
                            <div>
                                <label className="label-dark">Boylam (Longitude)</label>
                                <input type="number" step="any" className="input-dark" placeholder="39.8262" value={hotel.longitude ?? ""} onChange={(e) => update("longitude", e.target.value === "" ? null : Number(e.target.value))} />
                            </div>
                        </div>
                        <div>
                            <label className="label-dark">Google Maps Embed URL (opsiyonel)</label>
                            <input className="input-dark" placeholder="https://maps.google.com/maps?q=...&output=embed" value={hotel.map_url || ""} onChange={(e) => update("map_url", e.target.value)} />
                        </div>
                    </div>

                    {/* AMENITIES */}
                    <div className="border border-border_green p-5 space-y-4">
                        <div className="text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-2">
                            <Sparkles className="w-3.5 h-3.5" /> Otel Olanakları & İkonlar
                        </div>
                        <p className="text-xs text-muted_green">
                            Hızlı seçim için aşağıdaki ikon kütüphanesinden olanak ekleyin veya kendiniz yazın. Otel kartlarında ikon olarak görünür.
                        </p>
                        {/* Icon picker */}
                        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 max-h-72 overflow-y-auto pr-1">
                            {AMENITY_DEFS.map((def) => {
                                const Icon = def.icon;
                                const selected = (hotel.amenities || []).some((a) => def.patterns.some((p) => p.test(a)));
                                return (
                                    <button
                                        key={def.key}
                                        type="button"
                                        onClick={() => {
                                            const list = [...(hotel.amenities || [])];
                                            if (selected) {
                                                const filtered = list.filter((a) => !def.patterns.some((p) => p.test(a)));
                                                update("amenities", filtered);
                                            } else {
                                                list.push(def.label);
                                                update("amenities", list);
                                            }
                                        }}
                                        className={`p-2.5 border flex flex-col items-center gap-1 text-center transition-colors ${
                                            selected ? "border-gold bg-gold/10 text-gold" : "border-border_green hover:border-gold/60 text-bone/80"
                                        }`}
                                        data-testid={`amenity-toggle-${def.key}-${hotel.id}`}
                                    >
                                        <Icon className="w-4 h-4" />
                                        <span className="text-[10px] leading-tight">{def.label}</span>
                                        {selected && <Check className="w-3 h-3 text-emerald-400" />}
                                    </button>
                                );
                            })}
                        </div>
                        {/* Custom amenity add */}
                        <div className="flex gap-2">
                            <input
                                type="text"
                                placeholder="Özel olanak ekle (Enter ile)..."
                                className="input-dark flex-1"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && e.currentTarget.value.trim()) {
                                        e.preventDefault();
                                        update("amenities", [...(hotel.amenities || []), e.currentTarget.value.trim()]);
                                        e.currentTarget.value = "";
                                    }
                                }}
                                data-testid={`custom-amenity-${hotel.id}`}
                            />
                        </div>
                        {/* Current list */}
                        {(hotel.amenities || []).length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-2 border-t border-border_green">
                                {(hotel.amenities || []).map((a, i) => (
                                    <span key={a} className="inline-flex items-center gap-1.5 bg-emerald_deep border border-gold/40 px-3 py-1 text-[11px]">
                                        {a}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const next = [...(hotel.amenities || [])];
                                                next.splice(i, 1);
                                                update("amenities", next);
                                            }}
                                            className="text-rose-400 hover:text-rose-500"
                                            aria-label="Sil"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* HOTEL FAQ */}
                    <div className="border border-border_green p-5 space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-2">
                                <HelpCircle className="w-3.5 h-3.5" /> Otele Özel SSS ({(hotel.faqs || []).length})
                            </div>
                            <button
                                type="button"
                                onClick={() => update("faqs", [...(hotel.faqs || []), { id: crypto.randomUUID(), question: "", answer: "" }])}
                                className="text-xs text-gold inline-flex items-center gap-1 hover:underline"
                                data-testid={`add-faq-${hotel.id}`}
                            >
                                <Plus className="w-3 h-3" /> Soru Ekle
                            </button>
                        </div>
                        <p className="text-xs text-muted_green">
                            Bu otele özel soru-cevaplar otel detay sayfasındaki SSS bölümünde gösterilir. Boş bırakılırsa genel SSS gösterilir.
                        </p>
                        {(hotel.faqs || []).map((f, fi) => (
                            <div key={f.id || fi} className="border border-border_green/60 p-3 space-y-2">
                                <div className="flex gap-2">
                                    <input
                                        className="input-dark flex-1"
                                        placeholder="Soru (örn: Otelden Harem'e servis var mı?)"
                                        value={f.question}
                                        onChange={(e) => {
                                            const n = [...(hotel.faqs || [])];
                                            n[fi] = { ...n[fi], question: e.target.value };
                                            update("faqs", n);
                                        }}
                                        data-testid={`faq-q-${hotel.id}-${fi}`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const n = [...(hotel.faqs || [])];
                                            n.splice(fi, 1);
                                            update("faqs", n);
                                        }}
                                        className="text-red-400 hover:text-red-300"
                                        title="Soruyu sil"
                                        data-testid={`faq-del-${hotel.id}-${fi}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                                <textarea
                                    className="input-dark"
                                    rows={2}
                                    placeholder="Cevap"
                                    value={f.answer}
                                    onChange={(e) => {
                                        const n = [...(hotel.faqs || [])];
                                        n[fi] = { ...n[fi], answer: e.target.value };
                                        update("faqs", n);
                                    }}
                                    data-testid={`faq-a-${hotel.id}-${fi}`}
                                />
                                <div className="pt-2 border-t border-border_green/40 space-y-2">
                                    <div className="text-[9px] uppercase tracking-[0.2em] text-muted_green">🇬🇧 İngilizce (opsiyonel)</div>
                                    <input
                                        className="input-dark"
                                        placeholder="Question (English)"
                                        value={f.question_en || ""}
                                        onChange={(e) => {
                                            const n = [...(hotel.faqs || [])];
                                            n[fi] = { ...n[fi], question_en: e.target.value };
                                            update("faqs", n);
                                        }}
                                        data-testid={`faq-q-en-${hotel.id}-${fi}`}
                                    />
                                    <textarea
                                        className="input-dark"
                                        rows={2}
                                        placeholder="Answer (English)"
                                        value={f.answer_en || ""}
                                        onChange={(e) => {
                                            const n = [...(hotel.faqs || [])];
                                            n[fi] = { ...n[fi], answer_en: e.target.value };
                                            update("faqs", n);
                                        }}
                                        data-testid={`faq-a-en-${hotel.id}-${fi}`}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ROOMS */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-xs uppercase tracking-[0.2em] text-gold">Oda Tipleri & Fiyatlar (SAR)</div>
                            <button onClick={addRoom} className="text-xs text-gold inline-flex items-center gap-1 hover:underline" data-testid={`add-room-${hotel.id}`}>
                                <Plus className="w-3 h-3" /> Oda Tipi Ekle
                            </button>
                        </div>
                        <div className="space-y-4">
                            {hotel.rooms.map((r, ri) => (
                                <div key={r.id || `room-${ri}`} className="border border-border_green/60">
                                    <div className="p-3 grid grid-cols-2 md:grid-cols-[1fr_80px_95px_95px_75px_36px] gap-2 items-end">
                                        <div className="col-span-2 md:col-span-1">
                                            <label className="text-[10px] text-muted_green uppercase tracking-[0.14em]">Oda Adı</label>
                                            <input className="input-dark py-1" value={r.name} onChange={(e) => updateRoom(ri, "name", e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="text-[10px] text-muted_green uppercase tracking-[0.14em]">Kapasite</label>
                                            <input type="number" className="input-dark py-1 text-right" value={r.capacity} onChange={(e) => updateRoom(ri, "capacity", e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="text-[10px] text-muted_green uppercase tracking-[0.14em]">Bireysel</label>
                                            <input type="number" className="input-dark py-1 text-right" value={r.price_individual} onChange={(e) => updateRoom(ri, "price_individual", e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="text-[10px] text-muted_green uppercase tracking-[0.14em]">Toplu</label>
                                            <input type="number" className="input-dark py-1 text-right" value={r.price_bulk} onChange={(e) => updateRoom(ri, "price_bulk", e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="text-[10px] text-muted_green uppercase tracking-[0.14em]">Stok</label>
                                            <input type="number" className="input-dark py-1 text-right" value={r.inventory} onChange={(e) => updateRoom(ri, "inventory", e.target.value)} />
                                        </div>
                                        <button onClick={() => removeRoom(ri)} className="text-red-400 hover:text-red-500 pb-2 justify-self-end" data-testid={`del-room-${hotel.id}-${ri}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <div className="px-3 py-3 bg-emerald_deep/30 border-t border-border_green/40">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="text-[10px] uppercase tracking-[0.18em] text-gold font-semibold">Yemek Seçenekleri (kişi başı/gece)</div>
                                                        <div className="flex items-center gap-2">
                                                            {(!r.meal_plans || r.meal_plans.length === 0) && (
                                                                <button type="button" onClick={() => seedDefaultMealPlans(ri)} className="text-[10px] uppercase tracking-[0.15em] text-muted_green hover:text-gold" data-testid={`seed-meals-${hotel.id}-${ri}`}>
                                                                    + Kahvaltı/Yarım/Tam ekle
                                                                </button>
                                                            )}
                                                            <button type="button" onClick={() => addMealPlan(ri)} className="text-[10px] uppercase tracking-[0.15em] text-gold hover:underline inline-flex items-center gap-1" data-testid={`add-meal-${hotel.id}-${ri}`}>
                                                                <Plus className="w-3 h-3" /> Yeni
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {(r.meal_plans || []).length === 0 ? (
                                                        <div className="text-[11px] text-muted_green italic">Henüz yemek seçeneği eklenmemiş. "Yeni" ile özel bir seçenek ekleyebilir veya yandaki kısayolu kullanabilirsiniz.</div>
                                                    ) : (
                                                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                                            {(r.meal_plans || []).map((mp, mi) => (
                                                                <div key={mp.id || mi} className="flex items-center gap-1.5 bg-emerald_surface border border-border_green px-2 py-1.5" data-testid={`meal-row-${hotel.id}-${ri}-${mi}`}>
                                                                    <input
                                                                        className="input-dark py-1 flex-1 min-w-0 text-xs"
                                                                        value={mp.label}
                                                                        onChange={(e) => updateMealPlan(ri, mi, "label", e.target.value)}
                                                                        placeholder="Ad (örn: İftar)"
                                                                        data-testid={`meal-label-${hotel.id}-${ri}-${mi}`}
                                                                    />
                                                                    <input
                                                                        type="number"
                                                                        className="input-dark py-1 text-right w-20 text-xs"
                                                                        value={mp.price}
                                                                        onChange={(e) => updateMealPlan(ri, mi, "price", e.target.value)}
                                                                        placeholder="0"
                                                                        data-testid={`meal-price-${hotel.id}-${ri}-${mi}`}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => removeMealPlan(ri, mi)}
                                                                        className="text-rose-400 hover:text-rose-500 flex-shrink-0"
                                                                        title="Sil"
                                                                        data-testid={`del-meal-${hotel.id}-${ri}-${mi}`}
                                                                    >
                                                                        <X className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    <div className="mt-4 pt-4 border-t border-border_green/60">
                                                        <div className="text-[10px] uppercase tracking-[0.18em] text-gold font-semibold mb-2">Aylık Fiyat Uygula</div>
                                                        <div className="grid sm:grid-cols-2 gap-2 mb-2">
                                                            <div>
                                                                <label className="text-[10px] text-muted_green uppercase tracking-[0.14em]">Bireysel (SAR/gece)</label>
                                                                <input type="number" className="input-dark py-1 text-xs" value={getSeasonalForm(ri).price_individual} onChange={(e) => updateSeasonalForm(ri, { price_individual: e.target.value })} placeholder="Örn: 350" data-testid={`sp-pi-${hotel.id}-${ri}`} />
                                                            </div>
                                                            <div>
                                                                <label className="text-[10px] text-muted_green uppercase tracking-[0.14em]">Toplu (SAR/gece)</label>
                                                                <input type="number" className="input-dark py-1 text-xs" value={getSeasonalForm(ri).price_bulk} onChange={(e) => updateSeasonalForm(ri, { price_bulk: e.target.value })} placeholder="Örn: 280" data-testid={`sp-pb-${hotel.id}-${ri}`} />
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-wrap gap-1 mb-2 items-center">
                                                            {["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"].map((mn, mi) => {
                                                                const monthNum = mi + 1;
                                                                const active = getSeasonalForm(ri).months?.[monthNum];
                                                                const existing = (r.seasonal_prices || []).find((s) => s.month === monthNum);
                                                                return (
                                                                    <button key={monthNum} type="button" onClick={() => toggleMonth(ri, monthNum)}
                                                                        className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 border transition-colors ${
                                                                            active ? "bg-gold text-emerald_deep border-gold"
                                                                            : existing ? "border-emerald-500/60 text-emerald-300 hover:border-gold"
                                                                            : "border-border_green text-muted_green hover:border-gold hover:text-gold"
                                                                        }`}
                                                                        title={existing ? `Kayıtlı — Bireysel: ${existing.price_individual}, Toplu: ${existing.price_bulk}` : ""}
                                                                        data-testid={`sp-month-${hotel.id}-${ri}-${monthNum}`}
                                                                    >
                                                                        {mn}
                                                                    </button>
                                                                );
                                                            })}
                                                            <button type="button" onClick={() => applySeasonal(ri)} className="ml-auto text-[10px] uppercase tracking-[0.15em] px-3 py-1 bg-gold text-emerald_deep font-bold hover:brightness-95" data-testid={`sp-apply-${hotel.id}-${ri}`}>
                                                                Uygula
                                                            </button>
                                                        </div>
                                                        {(r.seasonal_prices || []).length > 0 && (
                                                            <div className="flex flex-wrap gap-1.5 pt-2 border-t border-border_green/50">
                                                                {(r.seasonal_prices || []).map((s) => {
                                                                    const monthNames = ["", "Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
                                                                    return (
                                                                        <span key={s.month} className="inline-flex items-center gap-1.5 bg-emerald_deep/60 border border-emerald-500/40 text-emerald-200 text-[10px] px-2 py-0.5" data-testid={`sp-tag-${hotel.id}-${ri}-${s.month}`}>
                                                                            <span className="uppercase tracking-[0.15em] font-semibold">{monthNames[s.month]}</span>
                                                                            <span className="font-mono">{s.price_individual}</span>
                                                                            <span className="text-muted_green">/</span>
                                                                            <span className="font-mono">{s.price_bulk}</span>
                                                                            <button onClick={() => removeSeasonal(ri, s.month)} className="text-rose-400 hover:text-rose-500 ml-0.5">
                                                                                <X className="w-3 h-3" />
                                                                            </button>
                                                                        </span>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="mt-4 pt-4 border-t border-border_green/60">
                                                        <div className="text-[10px] uppercase tracking-[0.18em] text-gold font-semibold mb-2">Tarih Aralığı Fiyatı</div>
                                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                                                            <div>
                                                                <label className="text-[10px] text-muted_green uppercase tracking-[0.14em]">Başlangıç</label>
                                                                <input type="date" className="input-dark py-1 text-xs" value={getRangeForm(ri).start} onChange={(e) => updateRangeForm(ri, { start: e.target.value })} data-testid={`rp-start-${hotel.id}-${ri}`} />
                                                            </div>
                                                            <div>
                                                                <label className="text-[10px] text-muted_green uppercase tracking-[0.14em]">Bitiş</label>
                                                                <input type="date" className="input-dark py-1 text-xs" value={getRangeForm(ri).end} onChange={(e) => updateRangeForm(ri, { end: e.target.value })} data-testid={`rp-end-${hotel.id}-${ri}`} />
                                                            </div>
                                                            <div>
                                                                <label className="text-[10px] text-muted_green uppercase tracking-[0.14em]">Bireysel (SAR)</label>
                                                                <input type="number" className="input-dark py-1 text-xs" value={getRangeForm(ri).price_individual} onChange={(e) => updateRangeForm(ri, { price_individual: e.target.value })} placeholder="Örn: 450" data-testid={`rp-pi-${hotel.id}-${ri}`} />
                                                            </div>
                                                            <div>
                                                                <label className="text-[10px] text-muted_green uppercase tracking-[0.14em]">Toplu (SAR)</label>
                                                                <input type="number" className="input-dark py-1 text-xs" value={getRangeForm(ri).price_bulk} onChange={(e) => updateRangeForm(ri, { price_bulk: e.target.value })} placeholder="Örn: 380" data-testid={`rp-pb-${hotel.id}-${ri}`} />
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center justify-between gap-2 mb-2">
                                                            <div className="text-[10px] text-muted_green">Belirtilen aralıktaki geceler için aylık fiyatın önüne geçer.</div>
                                                            <button type="button" onClick={() => applyRange(ri)} className="text-[10px] uppercase tracking-[0.15em] px-3 py-1 bg-gold text-emerald_deep font-bold hover:brightness-95" data-testid={`rp-apply-${hotel.id}-${ri}`}>
                                                                Ekle
                                                            </button>
                                                        </div>
                                                        {(r.date_range_prices || []).length > 0 && (
                                                            <div className="flex flex-wrap gap-1.5 pt-2 border-t border-border_green/50">
                                                                {(r.date_range_prices || []).map((rp, rpi) => (
                                                                    <span key={`${rp.start}|${rp.end}|${rpi}`} className="inline-flex items-center gap-1.5 bg-emerald_deep/60 border border-gold/40 text-bone text-[10px] px-2 py-0.5" data-testid={`rp-tag-${hotel.id}-${ri}-${rpi}`}>
                                                                        <span className="font-mono">{rp.start} → {rp.end}</span>
                                                                        <span className="font-mono text-gold">{rp.price_individual}</span>
                                                                        <span className="text-muted_green">/</span>
                                                                        <span className="font-mono text-gold">{rp.price_bulk}</span>
                                                                        <button onClick={() => removeRange(ri, rpi)} className="text-rose-400 hover:text-rose-500 ml-0.5">
                                                                            <X className="w-3 h-3" />
                                                                        </button>
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-between items-center gap-3 pt-4 border-t border-border_green">
                        <button onClick={() => onDelete(hotel)} className="text-rose-400 hover:text-rose-500 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-1.5" data-testid={`delete-hotel-${hotel.id}`}>
                            <Trash2 className="w-3.5 h-3.5" /> Oteli Sil
                        </button>
                        <button onClick={() => onSave(hotel)} className="btn-gold" data-testid={`save-hotel-${hotel.id}`}>
                            <Save className="w-4 h-4" /> Otel Bilgilerini Kaydet
                        </button>
                    </div>
                </div>
            )}
            <LocationPicker
                open={locPicker.open}
                city={hotel.city}
                initialLat={hotel.latitude ?? undefined}
                initialLng={hotel.longitude ?? undefined}
                onClose={() => setLocPicker({ open: false, idx: 0 })}
                onSelect={(la, ln) => {
                    const n = [...hotels];
                    n[idx].latitude = la;
                    n[idx].longitude = ln;
                    setHotels(n);
                }}
            />
        </div>
    );
};

const AdminHotels = () => {
    const { t } = useApp();
    const [hotels, setHotels] = useState([]);
    const [cityFilter, setCityFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [newHotel, setNewHotel] = useState({ name: "", city: "Mecca", star_rating: 5, distance_to_haram: "200m", description: "" });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/hotels");
            setHotels(data);
        } catch {
            toast.error("Oteller yüklenemedi");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const saveHotel = async (h) => {
        try {
            await api.put(`/admin/hotels/${h.id}`, h);
            toast.success(`${h.name} güncellendi`);
            load();
        } catch (e) {
            toast.error("Hata: " + (e.response?.data?.detail || ""));
        }
    };

    const deleteHotel = async (h) => {
        if (!window.confirm(`"${h.name}" otelini silmek istiyor musunuz? Bu işlem geri alınamaz.`)) return;
        try {
            await api.delete(`/admin/hotels/${h.id}`);
            toast.success("Otel silindi");
            load();
        } catch (e) {
            toast.error("Silinemedi: " + (e.response?.data?.detail || ""));
        }
    };

    const createHotel = async () => {
        if (!newHotel.name.trim()) { toast.error("Otel adı gerekli"); return; }
        try {
            await api.post("/admin/hotels", {
                ...newHotel,
                latitude: newHotel.city === "Mecca" ? 21.4225 : 24.4672,
                longitude: newHotel.city === "Mecca" ? 39.8262 : 39.6111,
                amenities: ["Ücretsiz WiFi", "Klima", "7/24 Resepsiyon"],
                images: [],
                rooms: [],
            });
            toast.success("Otel oluşturuldu");
            setCreating(false);
            setNewHotel({ name: "", city: "Mecca", star_rating: 5, distance_to_haram: "200m", description: "" });
            load();
        } catch (e) {
            toast.error("Oluşturulamadı: " + (e.response?.data?.detail || ""));
        }
    };

    const filtered = cityFilter === "all" ? hotels : hotels.filter((h) => h.city === cityFilter);

    return (
        <div>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <h1 className="font-serif text-4xl mb-2">{t("admin.hotels")}</h1>
                    <p className="text-muted_green">Fotoğraf, konum, oda tipleri ve fiyatları yönetin</p>
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                    {[
                        { v: "all", l: `Tümü (${hotels.length})` },
                        { v: "Mecca", l: `Mekke (${hotels.filter((h) => h.city === "Mecca").length})` },
                        { v: "Medina", l: `Medine (${hotels.filter((h) => h.city === "Medina").length})` },
                    ].map((f) => (
                        <button key={f.v} onClick={() => setCityFilter(f.v)}
                            className={`text-xs uppercase tracking-[0.15em] px-3 py-2 border font-semibold ${
                                cityFilter === f.v ? "bg-gold text-emerald_deep border-gold" : "border-border_green text-bone hover:border-gold"
                            }`}
                            data-testid={`city-filter-${f.v}`}>
                            {f.l}
                        </button>
                    ))}
                    <button onClick={() => setCreating(!creating)} className="btn-gold !text-xs !py-2 !px-3" data-testid="new-hotel-btn">
                        <Plus className="w-3.5 h-3.5" /> Yeni Otel
                    </button>
                </div>
            </div>

            {creating && (
                <div className="border border-gold/50 bg-emerald_surface p-5 mb-5 space-y-3" data-testid="new-hotel-form">
                    <div className="text-xs uppercase tracking-[0.2em] text-gold mb-2">Yeni Otel Oluştur</div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div>
                            <label className="label-dark">Otel Adı *</label>
                            <input className="input-dark" value={newHotel.name} onChange={(e) => setNewHotel({ ...newHotel, name: e.target.value })} placeholder="V12 Arabia" data-testid="new-hotel-name" />
                        </div>
                        <div>
                            <label className="label-dark">Şehir</label>
                            <select className="input-dark" value={newHotel.city} onChange={(e) => setNewHotel({ ...newHotel, city: e.target.value })}>
                                <option value="Mecca" className="bg-emerald_surface">Mekke</option>
                                <option value="Medina" className="bg-emerald_surface">Medine</option>
                            </select>
                        </div>
                        <div>
                            <label className="label-dark">Yıldız</label>
                            <select className="input-dark" value={newHotel.star_rating} onChange={(e) => setNewHotel({ ...newHotel, star_rating: Number(e.target.value) })}>
                                {[3, 4, 5].map((s) => <option key={s} value={s} className="bg-emerald_surface">{s} ★</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="label-dark">Harem'e Mesafe</label>
                            <input className="input-dark" value={newHotel.distance_to_haram} onChange={(e) => setNewHotel({ ...newHotel, distance_to_haram: e.target.value })} placeholder="200m" />
                        </div>
                    </div>
                    <div>
                        <label className="label-dark">Açıklama</label>
                        <textarea rows={2} className="input-dark" value={newHotel.description} onChange={(e) => setNewHotel({ ...newHotel, description: e.target.value })} placeholder="Kısa açıklama..." />
                    </div>
                    <div className="flex gap-2 justify-end">
                        <button onClick={() => setCreating(false)} className="text-xs text-bone/70 hover:text-bone uppercase tracking-[0.15em]">İptal</button>
                        <button onClick={createHotel} className="btn-gold !text-xs" data-testid="create-hotel-confirm">
                            <Plus className="w-3.5 h-3.5" /> Oteli Oluştur
                        </button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-muted_green text-sm">Yükleniyor…</div>
            ) : (
                <div className="space-y-3">
                    {filtered.map((h) => (
                        <HotelEditor
                            key={h.id}
                            hotel={h}
                            idx={hotels.indexOf(h)}
                            hotels={hotels}
                            setHotels={setHotels}
                            onSave={saveHotel}
                            onDelete={deleteHotel}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminHotels;
